import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@design-system';
import { EditAssumptionsDialog } from '@/features/edit-assumptions';
import { PoliciesComparisonModal } from '@/features/policies-comparison';
import { AddPartnerDialog, InvitePartnerDialog, LinkExistingPartnerDialog } from '@/features/add-partner';

import { Columns3, EllipsisVertical, Link, Mail, Settings2, Sparkles, UserPlus, X } from 'lucide-react';

import { useState } from 'react';
import { useSelector } from 'react-redux';

import { SessionType } from '@shared';
import { selectClient, selectSession, selectFeatureFlags } from '@/model';
import { EdjPilotRestricted } from '@client/shared/components/edj-pilot-restricted';

interface ShopInsuranceHeaderProps {
  isLoading: boolean;
  error: QuoteError | null;
  onClose: () => void;
}

export function ShopInsuranceHeader({ isLoading, error, onClose }: ShopInsuranceHeaderProps) {
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const hasPolicies = Object.keys(client?.multipleFundingSources?.ltcPolicy ?? {}).length > 0;
  const hasPartner = !!client?.partnerClientId;
  const isAdvisor = sessionType === SessionType.advisor || sessionType === SessionType.wizard;
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;
  const [openAddPartner, setOpenAddPartner] = useState(false);
  const [openInvitePartner, setOpenInvitePartner] = useState(false);
  const [openLinkExisting, setOpenLinkExisting] = useState(false);
  const [openEditAssumptions, setOpenEditAssumptions] = useState(false);
  const [openComparePolicies, setOpenComparePolicies] = useState(false);

  const description = isLoading
    ? 'Loading available insurance quotes...'
    : error !== null
      ? 'No quotes available'
      : 'Compare available insurance quotes';

  return (
    <div className="py-4">
      <div className="flex items-start justify-between">
        <div className="text-left">
          <h1 className="text-primary-dark flex items-center gap-2 text-lg font-semibold md:text-xl">
            Explore Coverage Options
            <Badge>
              <Sparkles />
              BETA
            </Badge>
          </h1>
          <p className="text-foreground text-sm md:text-base">{description}</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger className="focus-visible:outline-none">
              <Button
                variant="outline"
                size="icon-sm"
                className="rounded-full"
              >
                <EllipsisVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setOpenEditAssumptions(true)}>
                <Settings2 />
                Edit Assumptions
              </DropdownMenuItem>
              {hasPolicies && (
                <DropdownMenuItem onClick={() => setOpenComparePolicies(true)}>
                  <Columns3 />
                  Compare Policies
                </DropdownMenuItem>
              )}
              {!hasPartner && !isD2C && isAdvisor && (
                <DropdownMenuItem onClick={() => setOpenLinkExisting(true)}>
                  <Link />
                  Link Existing Client as Partner
                </DropdownMenuItem>
              )}
              {!hasPartner && !isD2C && (
                <EdjPilotRestricted>
                  <DropdownMenuItem onClick={() => setOpenAddPartner(true)}>
                    <UserPlus />
                    Add new partner
                  </DropdownMenuItem>
                </EdjPilotRestricted>
              )}
              {!hasPartner && !isD2C && !isAdvisor && (
                <DropdownMenuItem onClick={() => setOpenInvitePartner(true)}>
                  <Mail />
                  Invite Partner
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={onClose}
            aria-label="Close"
            className="rounded-full"
          >
            <X />
          </Button>
        </div>
      </div>

      <AddPartnerDialog
        open={openAddPartner}
        onOpenChange={setOpenAddPartner}
        clientId={client.clientId}
      />

      <InvitePartnerDialog
        open={openInvitePartner}
        onOpenChange={setOpenInvitePartner}
      />

      {isAdvisor && (
        <LinkExistingPartnerDialog
          open={openLinkExisting}
          client={client}
          onClose={() => setOpenLinkExisting(false)}
        />
      )}

      <EditAssumptionsDialog
        open={openEditAssumptions}
        onOpenChange={setOpenEditAssumptions}
      />

      <PoliciesComparisonModal
        open={openComparePolicies}
        onOpenChange={setOpenComparePolicies}
      />
    </div>
  );
}
